import { getConfig, hydrateAppConfig, installConfig } from './app/bootstrap';
import { renderFrontendOwnedShell } from './app/renderShell';
import { loadTypeScriptBeatLineRuntime } from './runtime/legacyTsRuntime';
import { installChecklistPatches } from './ui/checklistPatches';
import { applyBackendStatePatches } from './ui/backendStatePatches';
import { installSpecialModePatches } from './ui/specialModePatches';
import { installLocalDebug } from './ui/localDebug';
import { installStartScreenController } from './ui/startScreen';
import { installProfileController } from './ui/profile';
import { installNativeBridgeDiagnostics, installNativeRuntimeClasses, installNativeNavigationGuard, markNativeAppReady } from './native/bridge';
import { installNativeEmailAuth, resumeNativeEmailSession } from './native/emailAuth';
import type { BeatLineViteConfig } from './api/types';
import { installBeatLineFullRestBridge } from './api/fullRestBridge';

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function waitForStableFirstPaint(): Promise<void> {
  // Local native app: keep the built-in loader over the app until the shell,
  // runtime and first layout pass have settled. This masks the old startscreen
  // mis-layout flash without relying on WordPress/wp_head.
  await nextFrame();
  await nextFrame();
  await nextFrame();
}

function finishBoot(config?: BeatLineViteConfig): void {
  try { document.documentElement.classList.remove('beatline-vite-booting'); } catch (_) {}
  try { document.body?.classList.remove('beatline-vite-booting'); } catch (_) {}
  try { document.getElementById('beatline-vite-preloader')?.remove(); } catch (_) {}
  markNativeAppReady(config);
}


function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderBootstrapError(config: BeatLineViteConfig): void {
  const root = document.getElementById('beatline-vite-root') || document.body;
  const error = config.bootstrapStatus?.error || 'Geen geldige backend-config ontvangen.';
  root.innerHTML = `<div style="min-height:100vh;background:radial-gradient(circle at 50% 18%,rgba(255,138,0,.18),transparent 30%),linear-gradient(180deg,#101a2d,#07101f);color:white;font-family:Arial,sans-serif;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;box-sizing:border-box;"><div style="max-width:520px;background:rgba(8,13,26,.78);border:1px solid rgba(255,255,255,.12);border-radius:24px;padding:24px;box-shadow:0 18px 60px rgba(0,0,0,.38);"><h1 style="margin:0 0 10px;font-size:28px;">BeatLine backend niet gekoppeld</h1><p style="opacity:.85;line-height:1.45;margin:0 0 14px;">De lokale app is gestart, maar kon de WordPress backend-config niet ophalen. Upload eerst de C50 loader naar <strong>mu-plugins</strong> en rebuild daarna de app.</p><p style="font-size:12px;opacity:.65;word-break:break-word;margin:0;">${escapeHtml(error)}</p></div></div>`;
}

async function main() {
  const initialConfig = getConfig();
  const hydratedConfig = await hydrateAppConfig(initialConfig);
  const config = await resumeNativeEmailSession(hydratedConfig);
  installConfig(config);
  installNativeRuntimeClasses(config);
  installNativeNavigationGuard(config);
  installNativeEmailAuth(config);
  if (config.nativeApp && (!config.bootstrapStatus?.ok || !config.beatlineSettings?.nonce)) {
    renderBootstrapError(config);
    installNativeBridgeDiagnostics(config);
    installLocalDebug(config);
    finishBoot(config);
    return;
  }
  await renderFrontendOwnedShell(config);
  applyBackendStatePatches(config);
  installChecklistPatches();
  installStartScreenController(config);
  installProfileController(config);
  installSpecialModePatches();
  installBeatLineFullRestBridge(config);
  await loadTypeScriptBeatLineRuntime(config);
  installNativeBridgeDiagnostics(config);
  installLocalDebug(config);
  await waitForStableFirstPaint();
  finishBoot(config);
  if (config.debug) {
    console.info('[BeatLine Vite C50] local Capacitor/Vite runtime ready', {
      version: config.version,
      ajaxUrl: config.beatlineSettings.ajaxUrl,
      hasNonce: !!config.beatlineSettings.nonce,
      nativeApp: !!config.nativeApp,
      entryContext: config.entryContext,
      backend: 'active BEATLINE plugin via admin-ajax.php',
      runtime: 'C50 local-app path: backend bootstrap required; WordPress is API/backend only.'
    });
  }
}

main().catch((error) => {
  console.error('[BeatLine Vite C50] startup failed', error);
  finishBoot();
  const root = document.getElementById('beatline-vite-root') || document.body;
  root.innerHTML = `<div style="min-height:100vh;background:#08101f;color:white;font-family:Arial,sans-serif;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;"><div><h1>BeatLine kon niet starten</h1><p>${String(error?.message || error)}</p></div></div>`;
});

